import PageLoading from 'components/PageLoading';
import useNotification, {
  MIN_NUMBER_OF_NOTIFICATIONS_TO_ALLOW_PAGINATION,
  NOTIFICATION_LIMIT_PER_PAGE,
  NOTIFICATION_TITLE_MAX_NUMBER_OF_CHARACTERS,
  PANEL_NOTIFICATION_DESCRIPTION_MAX_NUMBER_OF_CHARACTERS,
} from 'features/useNotification';
import {Notification, Pagination} from 'letrus-ui';
import moment from 'moment';
import 'moment/locale/pt-br';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  fetchNotificationRequest,
  updateNotificationById,
} from 'APIs/letrusAPI/notification';
import {NotificationItemProps} from 'letrus-ui/dist/components/features/Notification/NotificationItem';
import {NotificationLog} from 'APIs/letrusAPI/notification/types';
import styles from './NotificationsHistory.module.scss';

moment.locale('pt-br');

function NotificationsHistory(): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const {getLinkType, hasBeenSeen, getNotificationSeenState} =
    useNotification();

  const {
    data: notificationData,
    isLoading: isLoadingNotifications,
    mutate,
  } = fetchNotificationRequest({
    limit: NOTIFICATION_LIMIT_PER_PAGE,
    offset: (currentPage - 1) * NOTIFICATION_LIMIT_PER_PAGE,
    ordering: '-created',
  });

  const notifications = notificationData?.results || [];
  const notificationCount = notificationData?.count || 0;

  useEffect(() => {
    const notReceivedNotifications = notifications.filter(
      (notification) => !notification.received,
    );
    if (notReceivedNotifications.length) {
      Promise.all(
        notReceivedNotifications.map((notification) =>
          updateNotificationById(notification.id, {
            received: new Date().toISOString(),
          }),
        ),
      ).then(() => mutate());
    }
  }, [notifications]);

  const totalNumberOfPages = Math.ceil(
    notificationCount / NOTIFICATION_LIMIT_PER_PAGE,
  );
  const notificationList = notifications.map<NotificationItemProps>(
    (notification) => ({
      handleClick: () => onClickNotificationItem(notification),
      title: notification.title?.substring(
        0,
        NOTIFICATION_TITLE_MAX_NUMBER_OF_CHARACTERS,
      ),
      description: notification.content.substring(
        0,
        PANEL_NOTIFICATION_DESCRIPTION_MAX_NUMBER_OF_CHARACTERS,
      ),
      id: notification.id,
      opened: hasBeenSeen(notification),
      time: moment(notification.created).fromNow(),
      withHeaderIcon: !!notification.link,
      hasReceivedToday: false,
    }),
  );

  async function onClickNotificationItem(notification: NotificationLog) {
    const notificationSeenState = getNotificationSeenState(notification);
    const notificationLinkType = getLinkType(notification);

    if (notificationSeenState === 'not-received') {
      await updateNotificationById(notification.id, {
        received: new Date().toISOString(),
      });
      mutate();
    }

    if (notificationSeenState === 'not-opened') {
      await updateNotificationById(notification.id, {
        opened: new Date().toISOString(),
      });
      mutate();
    }

    if (!notification.link) {
      return;
    }

    if (notificationLinkType === 'internal') {
      navigate(notification.link);
    }

    if (notificationLinkType === 'external') {
      window.open(notification.link, '_blank');
    }
  }

  return (
    <div className={styles.container}>
      <h1>Histórico de notificações</h1>

      <main>
        <section>
          {isLoadingNotifications ? (
            <PageLoading />
          ) : (
            <Notification.List items={notificationList} />
          )}
        </section>
      </main>

      {notificationCount >= MIN_NUMBER_OF_NOTIFICATIONS_TO_ALLOW_PAGINATION && (
        <footer>
          <Pagination
            currentPage={currentPage}
            hasNext={currentPage < totalNumberOfPages}
            hasPrevious={currentPage > 1}
            totalPages={totalNumberOfPages}
            onChange={(page) => setCurrentPage(page)}
          />
        </footer>
      )}
    </div>
  );
}

export default NotificationsHistory;
